import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CloseOutlinedIcon, EditOutlinedIcon } from 'icons/materialIcons';
import avatarPlaceholder from 'images/avatar-cropped.png';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  CircularProgress,
  Avatar
} from '@mui/material';
import Modal from 'components/common/Modal';
import { TextRotateVerticalOutlinedIcon, CheckBoxOutlinedIcon, AttachFileOutlinedIcon } from 'icons/materialIcons';
import WhiteButtonWithIcon from 'components/common/button/WhiteButtonWithIcon';
import { saveLead } from 'apis/leadCaptureApi';
import { useAuth } from 'contexts/AuthContext';
import { useLeadCaptureContext } from 'contexts/LeadCaptureContext';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import PhoneInputField from 'components/common/PhoneInputField';
import { getCardsByUser } from 'apis/userApi';

const textFieldStyles = {
  backgroundColor: '#f7f7f7',
  borderRadius: '12px',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    '& fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '&:hover fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
      borderRadius: '12px',
    },
    '& input': {
      borderRadius: '12px',
      padding: '10px 12px',
    },
    '& input::placeholder': {
      fontSize: '0.8rem',
      fontWeight: '300',
    },
  },
};

const CreateLeadModal = ({ open, onClose }) => {
  const { authState } = useAuth();
  const { addContact } = useLeadCaptureContext();
  const [formData, setFormData] = useState({
    template: 'No Template',
    fullName: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
  });

  const [customFields, setCustomFields] = useState([]);
  const fileInputRefs = useRef({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const [userCards, setUserCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [loadingCards, setLoadingCards] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchUserCards = async () => {
      if (!authState?.user?.userId) return;

      setLoadingCards(true);
      try {
        const cards = await getCardsByUser(authState.user.userId);
        setUserCards(cards);

        // Find and set the default card as selected
        const defaultCard = cards.find(card => card.is_default);
        if (defaultCard) {
          setSelectedCardId(defaultCard._id);
        } else if (cards.length > 0) {
          // Fallback to first card if no default card found
          setSelectedCardId(cards[0]._id);
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to load cards',
          severity: 'error'
        });
      } finally {
        setLoadingCards(false);
      }
    };

    if (open) {
      fetchUserCards();
    }
  }, [authState?.user?.userId, open]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value) => {
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
    setPhoneError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Form validation - only name and email are required
    if (!formData.fullName.trim() || !formData.email.trim()) {
      setSnackbar({
        open: true,
        message: 'Name and email are required',
        severity: 'error'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare lead data
      const leadData = {
        leadName: formData.fullName.trim(),
        leadEmail: formData.email.trim(),
        // Only include leadPhoneNumber if it's not empty
        ...(formData.phone.trim() ? { leadPhoneNumber: formData.phone.trim() } : {}),
        leadJobTitle: formData.jobTitle.trim(),
        leadCompany: formData.company.trim(),
        leadNotes: '',
        leadSource: 'manually-added',
        userId: authState.user.userId,
        // Only include cardId if a card is selected
        ...(selectedCardId ? { cardId: selectedCardId } : {})
      };

      // Add custom fields if any
      if (customFields.length > 0) {
        leadData.customFields = {};
        customFields.forEach((field, index) => {
          if (field.type === 'text') {
            leadData.customFields[`field_${index}`] = {
              label: field.label,
              value: field.value,
              isRequired: false
            };
          } else if (field.type === 'checkbox') {
            leadData.customFields[`field_${index}`] = {
              label: field.label,
              value: field.checked ? 'Yes' : 'No',
              isRequired: false
            };
          } else if (field.type === 'file' && field.file) {
            leadData.customFields[`field_${index}`] = {
              label: field.label,
              value: field.file.name,
              isRequired: false
            };
          }
        });
      }

      const response = await saveLead(leadData);

      if (response && response.success) {
        addContact(response.lead);

        setSnackbar({
          open: true,
          message: 'Lead created successfully',
          severity: 'success'
        });

        setFormData({
          template: 'No Template',
          fullName: '',
          email: '',
          phone: '',
          jobTitle: '',
          company: '',
        });
        setCustomFields([]);

        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        throw new Error(response?.message || 'Failed to create lead');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to create lead. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addTextField = () => {
    setCustomFields([
      ...customFields,
      {
        type: 'text',
        label: 'Text Field',
        value: '',
        isLabelEditing: false
      }
    ]);
  };

  const addCheckbox = () => {
    setCustomFields([
      ...customFields,
      {
        type: 'checkbox',
        label: 'Checkbox',
        checked: false,
        isLabelEditing: false
      }
    ]);
  };

  const addFileAttachment = () => {
    const newField = {
      type: 'file',
      label: 'File Attachment',
      file: null,
      isLabelEditing: false
    };
    const newIndex = customFields.length;
    fileInputRefs.current[newIndex] = React.createRef();
    setCustomFields([...customFields, newField]);
  };

  const handleCustomFieldChange = (index, field, value) => {
    const newCustomFields = [...customFields];
    newCustomFields[index][field] = value;
    setCustomFields(newCustomFields);
  };

  const toggleLabelEditing = (index) => {
    const newCustomFields = [...customFields];
    newCustomFields[index].isLabelEditing = !newCustomFields[index].isLabelEditing;
    setCustomFields(newCustomFields);
  };

  const handleFileChange = (event, index) => {
    const file = event.target.files[0];
    handleCustomFieldChange(index, 'file', file);
  };

  const handleFileBoxClick = (index) => {
    if (fileInputRefs.current[index]) {
      fileInputRefs.current[index].current.click();
    }
  };

  const handleClose = () => {
    setCustomFields([]);
    setFormData({
      template: 'No Template',
      fullName: '',
      email: '',
      phone: '',
      jobTitle: '',
      company: '',
    });
    setSelectedCardId('');
    onClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <>
      <Modal open={open} onClose={handleClose} width="45%" height="80%" maxWidth={800}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', position: 'relative', p: 2 }}>
          <Box sx={{ pb: 0, mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: '1.5rem',
                color: '#4f4f4f',
              }}
            >
              Create New Lead
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={{
                padding: '4px',
                marginTop: '-8px',
                marginRight: '-8px',
                color: '#828282',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <CloseOutlinedIcon sx={{ fontSize: '1.7rem' }} />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pr: 2 }}>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Lead Owner
                  </Typography>
                  <FormControl fullWidth sx={{ ...textFieldStyles }}>
                    <Select
                      value={selectedCardId}
                      onChange={(e) => setSelectedCardId(e.target.value)}
                      disabled={loadingCards}
                      displayEmpty
                      sx={{
                        fontSize: '0.8rem',
                        borderRadius: '12px',
                        '& .MuiSelect-select': {
                          borderRadius: '12px',
                          padding: '10px 14px',
                        },
                        '& fieldset': {
                          border: 'none',
                          borderRadius: '12px',
                        }
                      }}
                    >
                      <MenuItem value="">
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Avatar
                            src={avatarPlaceholder}
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 2
                            }}
                          />
                          <Typography sx={{ color: '#999', fontSize: '0.8rem' }}>
                            No card selected
                          </Typography>
                        </Box>
                      </MenuItem>
                      {userCards.map((card) => (
                        <MenuItem key={card._id} value={card._id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            <Avatar
                              src={card.profilePictureUrl || avatarPlaceholder}
                              sx={{
                                width: 24,
                                height: 24,
                                mr: 2
                              }}
                            />
                            <Typography sx={{ fontSize: '0.8rem' }}>
                              {card.contact_info?.fullName || 'Unnamed Card'}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {loadingCards && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                      <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>
                        Loading cards...
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f' }}>
                      Full Name *
                    </Typography>
                    <Button
                      sx={{
                        backgroundColor: 'white',
                        color: '#4f4f4f',
                        borderRadius: '20px',
                        border: '1px solid #e3e3e3',
                        boxShadow: 'none',
                        textTransform: 'none',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        padding: '6px 16px',
                        '&:hover': {
                          backgroundColor: 'white',
                          boxShadow: 2,
                        },
                      }}
                    >
                      Create Test Lead
                    </Button>
                  </Box>
                  <TextField
                    fullWidth
                    required
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Full Name"
                    sx={textFieldStyles}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Email *
                  </Typography>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email"
                    sx={textFieldStyles}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Phone Number
                  </Typography>
                  <PhoneInputField
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    error={phoneError}
                    containerStyle={{ marginBottom: '10px' }}
                    placeholder="Enter phone number"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Job Title
                  </Typography>
                  <TextField
                    fullWidth
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleInputChange}
                    placeholder="Job Title"
                    sx={textFieldStyles}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Company
                  </Typography>
                  <TextField
                    fullWidth
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    placeholder="Company"
                    sx={textFieldStyles}
                  />
                </Grid>

                {customFields.map((field, index) => (
                  <Grid item xs={12} key={index} sx={{ mt: 2 }}>
                    {field.type === 'text' && (
                      <>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                          px: 0,
                          width: '100%'
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                display: 'inline-flex'
                              }}
                            >
                              {field.isLabelEditing ? (
                                <TextField
                                  value={field.label}
                                  onChange={(e) => handleCustomFieldChange(index, 'label', e.target.value)}
                                  onBlur={() => toggleLabelEditing(index)}
                                  autoFocus
                                  sx={{
                                    ...textFieldStyles,
                                    width: '50%',
                                    mr: 1,
                                  }}
                                />
                              ) : (
                                field.label
                              )}
                            </Typography>
                          </Box>
                          {!field.isLabelEditing && (
                            <IconButton
                              size="small"
                              onClick={() => toggleLabelEditing(index)}
                            >
                              <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                        </Box>
                        <TextField
                          fullWidth
                          placeholder={field.label}
                          value={field.value}
                          onChange={(e) => handleCustomFieldChange(index, 'value', e.target.value)}
                          sx={textFieldStyles}
                        />
                      </>
                    )}
                    {field.type === 'checkbox' && (
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Checkbox
                          checked={field.checked}
                          onChange={(e) => handleCustomFieldChange(index, 'checked', e.target.checked)}
                          sx={{ ml: -1 }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              color: '#4f4f4f'
                            }}
                          >
                            {field.isLabelEditing ? (
                              <TextField
                                value={field.label}
                                onChange={(e) => handleCustomFieldChange(index, 'label', e.target.value)}
                                onBlur={() => toggleLabelEditing(index)}
                                autoFocus
                                sx={{
                                  ...textFieldStyles,
                                  width: '50%',
                                  mr: 1,
                                }}
                              />
                            ) : (
                              field.label
                            )}
                          </Typography>
                          {!field.isLabelEditing && (
                            <IconButton
                              size="small"
                              onClick={() => toggleLabelEditing(index)}
                            >
                              <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )}
                    {field.type === 'file' && (
                      <>
                        <Box sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1,
                          px: 0,
                          width: '100%'
                        }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                color: '#4f4f4f',
                                display: 'inline-flex'
                              }}
                            >
                              {field.isLabelEditing ? (
                                <TextField
                                  value={field.label}
                                  onChange={(e) => handleCustomFieldChange(index, 'label', e.target.value)}
                                  onBlur={() => toggleLabelEditing(index)}
                                  autoFocus
                                  sx={{
                                    ...textFieldStyles,
                                    width: '50%',
                                    mr: 1,
                                  }}
                                />
                              ) : (
                                field.label
                              )}
                            </Typography>
                          </Box>
                          {!field.isLabelEditing && (
                            <IconButton
                              size="small"
                              onClick={() => toggleLabelEditing(index)}
                            >
                              <EditOutlinedIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Box
                          onClick={() => handleFileBoxClick(index)}
                          sx={{
                            ...textFieldStyles,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 1.5,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#efefef',
                            },
                          }}
                        >
                          <AttachFileOutlinedIcon sx={{ mr: 1, color: '#4f4f4f', fontSize: "16px" }} />
                          <Typography sx={{ fontSize: '0.8rem', color: '#4f4f4f' }}>
                            {field.file ? field.file.name : 'Attach a file'}
                          </Typography>
                          <input
                            type="file"
                            ref={fileInputRefs.current[index]}
                            onChange={(e) => handleFileChange(e, index)}
                            style={{ display: 'none' }}
                          />
                        </Box>
                      </>
                    )}
                  </Grid>
                ))}

                <Grid item xs={12} sx={{ mt: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#4f4f4f', mb: 1 }}>
                    Add Custom Fields
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <WhiteButtonWithIcon
                      icon={TextRotateVerticalOutlinedIcon}
                      text="Text Field"
                      onClick={addTextField}
                    />
                    <WhiteButtonWithIcon
                      icon={CheckBoxOutlinedIcon}
                      text="Checkbox"
                      onClick={addCheckbox}
                    />
                    <WhiteButtonWithIcon
                      icon={AttachFileOutlinedIcon}
                      text="File Attachment"
                      onClick={addFileAttachment}
                      iconProps={{ fontSize: "16px" }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 3,
              pt: 2,
              borderTop: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}>
              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  backgroundColor: 'white',
                  color: '#4f4f4f',
                  borderRadius: '20px',
                  border: '1px solid #e3e3e3',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  fontWeight: '600',
                  '&:hover': {
                    backgroundColor: 'white',
                    boxShadow: 2,
                  },
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                type="submit"
                sx={{
                  backgroundColor: '#000',
                  color: '#fff',
                  fontWeight: '600',
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontSize: '0.7rem',
                  border: '1px solid #000',
                  '&:hover': {
                    backgroundColor: '#000',
                    boxShadow: 2,
                  },
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Create Lead'
                )}
              </Button>
            </Box>
          </form>
        </Box>
      </Modal>

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        handleClose={handleSnackbarClose}
      />
    </>
  );
};

CreateLeadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateLeadModal;